import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSatelliteHistory, getSatelliteIndex } from './satelliteApi';
import {
  NDVI_STATUS,
  SEASON_HISTORY,
  normaliseIndex,
  normaliseIndexHistory,
  classifyLegend,
  buildWaterSignal,
  buildGrowthSignal,
  buildNdreSignal,
  buildNdwiSignal,
  buildVerdict,
  fieldPatches,
  morePatches,
  seasonToday,
  seasonWindows,
} from './satelliteModel';
import { analyseIndexImageFull } from './satellitePatches';

/**
 * Everything the Support page shows about one farm's satellite data.
 *
 * Asks exactly what the farmer app's farm page asks — NDMI, NDVI, NDRE and
 * NDWI with their pictures, and the 90-day NDVI timeline — plus the same 90
 * days in each of the last five years for "Season by season", whose current
 * season is that same 90-day answer. Ten requests, at most two at a time, in
 * that order, so the map's pictures come first. A 429 waits for the backend's
 * RateLimit-Reset (at most a minute) and tries once more, as the farmer app does.
 *
 * Nothing is asked until the page is open with a farm that has a boundary,
 * and nothing is kept once it closes: there is no prefetch and no cache here
 * (the backend remembers answers for hours).
 *
 * Signals, verdict and lettered patches are built exactly as the farmer app's
 * useFieldSignals builds them with `layers`.
 */

const INDICES = ['ndmi', 'ndvi', 'ndre', 'ndwi'];
const MAX_CONCURRENT = 2;
const MAX_RETRY_WAIT_S = 60;
const DEFAULT_RETRY_WAIT_S = 10;

const LOADING = { status: 'loading', data: null, classified: null, error: null };
const NO_ANALYSIS = { patches: [], pixels: null };

/** An answer's normalised data once it has arrived, else null. */
const readyData = (entry) => (entry && entry.status === 'ready' ? entry.data : null);

/** An index that failed, or that CropGen could not be asked for: worth a retry. */
const failed = (entry) =>
  Boolean(entry) && (entry.status === 'error' || (entry.status === 'ready' && entry.data.status === NDVI_STATUS.UNAVAILABLE));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withRatePolicy = async (call) => {
  try {
    return await call();
  } catch (err) {
    if (!err || !err.response || err.response.status !== 429) throw err;
    const reset = Number(err.response.headers && err.response.headers['ratelimit-reset']);
    const waitS = Number.isFinite(reset) && reset > 0 ? Math.min(reset, MAX_RETRY_WAIT_S) : DEFAULT_RETRY_WAIT_S;
    await sleep(waitS * 1000);
    return call();
  }
};

/** Runs the tasks in order, `limit` at a time. Each task handles its own errors. */
const runQueue = (tasks, limit, isCurrent) => {
  let next = 0;
  const worker = async () => {
    while (next < tasks.length && isCurrent()) {
      const task = tasks[next];
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      await task();
    }
  };
  return Promise.all(Array.from({ length: limit }, worker));
};

const initialIndices = () => Object.fromEntries(INDICES.map((i) => [i, LOADING]));
const initialSeasons = () =>
  seasonWindows(seasonToday()).map((w) => ({ ...w, status: 'loading', data: null, error: null }));

export default function useFarmSatelliteInsights(farm) {
  const enabled = Boolean(farm && farm.id != null && farm.hasBoundary);
  const [byIndex, setByIndex] = useState(initialIndices);
  const [analyses, setAnalyses] = useState({});
  const [history, setHistory] = useState({ status: 'loading', data: null, error: null });
  const [seasons, setSeasons] = useState(initialSeasons);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const runRef = useRef(0);
  const handledNonceRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const run = ++runRef.current;
    const current = () => run === runRef.current;
    // Only the run a reload() caused asks past the backend's cache; another farm is an ordinary request.
    const refresh = refreshNonce !== handledNonceRef.current;
    handledNonceRef.current = refreshNonce;
    const extra = refresh ? { refresh: true } : {};
    const windows = initialSeasons();

    setByIndex(initialIndices());
    setAnalyses({});
    setHistory({ status: 'loading', data: null, error: null });
    setSeasons(windows);

    const indexTask = (index) => async () => {
      try {
        const raw = await withRatePolicy(() => getSatelliteIndex(farm.id, index, extra));
        const data = normaliseIndex(raw);
        const classified = data.status === NDVI_STATUS.OK ? classifyLegend(data.legend, index) : null;
        if (!current()) return;
        setByIndex((prev) => ({ ...prev, [index]: { status: 'ready', data, classified, error: null } }));
        if (data.imageBase64 && classified) {
          // Reading the picture is local work: it does not hold a request slot.
          analyseIndexImageFull(data, classified, farm.ring, farm.acres)
            .catch(() => NO_ANALYSIS)
            .then((result) => {
              if (current()) setAnalyses((prev) => ({ ...prev, [index]: result }));
            });
        }
      } catch (error) {
        if (current()) setByIndex((prev) => ({ ...prev, [index]: { status: 'error', data: null, classified: null, error } }));
      }
    };

    const setSeason = (i, patch) => setSeasons((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
    const nowIndex = windows.findIndex((w) => w.isNow);

    const historyTask = async () => {
      try {
        const raw = await withRatePolicy(() =>
          getSatelliteHistory(farm.id, { index: 'ndvi', days: SEASON_HISTORY.windowDays, ...extra })
        );
        const data = normaliseIndexHistory(raw);
        if (!current()) return;
        setHistory({ status: 'ready', data, error: null });
        // The current season is this same 90-day answer.
        setSeason(nowIndex, { status: 'ready', data });
      } catch (error) {
        if (!current()) return;
        setHistory({ status: 'error', data: null, error });
        setSeason(nowIndex, { status: 'error', error });
      }
    };

    const seasonTask = (w, i) => async () => {
      try {
        const raw = await withRatePolicy(() =>
          getSatelliteHistory(farm.id, { index: 'ndvi', start: w.start, end: w.end, ...extra })
        );
        if (current()) setSeason(i, { status: 'ready', data: normaliseIndexHistory(raw) });
      } catch (error) {
        if (current()) setSeason(i, { status: 'error', error });
      }
    };

    runQueue(
      [
        indexTask('ndmi'),
        indexTask('ndvi'),
        historyTask,
        indexTask('ndre'),
        indexTask('ndwi'),
        ...windows.map((w, i) => (w.isNow ? null : seasonTask(w, i))).filter(Boolean),
      ],
      MAX_CONCURRENT,
      current
    );

    return () => {
      // Answers for a page that has closed or reloaded are dropped.
      runRef.current += 1;
    };
    // The farm's ring and area belong to its id; a new id is what starts a new run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, farm?.id, refreshNonce]);

  const { ndmi, ndvi, ndre, ndwi } = byIndex;
  const analysisOf = (index) => analyses[index] || NO_ANALYSIS;
  const ndmiAnalysis = analysisOf('ndmi');
  const ndviAnalysis = analysisOf('ndvi');
  const ndreAnalysis = analysisOf('ndre');
  const ndwiAnalysis = analysisOf('ndwi');

  const water = useMemo(
    () => buildWaterSignal({ classified: ndmi && ndmi.classified, date: readyData(ndmi)?.date, patches: ndmiAnalysis.patches }),
    [ndmi, ndmiAnalysis.patches]
  );
  const growth = useMemo(
    () =>
      buildGrowthSignal({
        ndvi: ndvi && ndvi.classified,
        ndre: ndre && ndre.classified,
        date: readyData(ndvi)?.date,
        patches: ndviAnalysis.patches,
      }),
    [ndvi, ndre, ndviAnalysis.patches]
  );
  const verdict = useMemo(() => buildVerdict([water, growth]), [water, growth]);
  const basePatches = useMemo(() => fieldPatches([water, growth]), [water, growth]);
  const more = useMemo(() => {
    const leafSignal = buildNdreSignal({ classified: ndre && ndre.classified, date: readyData(ndre)?.date, patches: ndreAnalysis.patches });
    const ndwiSignal = buildNdwiSignal({ classified: ndwi && ndwi.classified, date: readyData(ndwi)?.date, patches: ndwiAnalysis.patches });
    return { ndre: leafSignal, ndwi: ndwiSignal, patches: morePatches(basePatches, [leafSignal, ndwiSignal]) };
  }, [ndre, ndwi, ndreAnalysis.patches, ndwiAnalysis.patches, basePatches]);

  /** By the page's layer ids (the farmer app's names): growth = NDVI, water = NDMI. */
  const entries = useMemo(() => ({ growth: ndvi, ndre, water: ndmi, ndwi }), [ndvi, ndre, ndmi, ndwi]);
  const signals = useMemo(() => ({ growth, water, ndre: more.ndre, ndwi: more.ndwi }), [growth, water, more]);
  const patches = useMemo(() => [...basePatches, ...more.patches], [basePatches, more.patches]);
  const pixels = {
    water: ndmiAnalysis.pixels,
    growth: ndviAnalysis.pixels,
    ndre: ndreAnalysis.pixels,
    ndwi: ndwiAnalysis.pixels,
  };

  const reload = useCallback(() => setRefreshNonce((n) => n + 1), []);

  return {
    enabled,
    entries,
    signals,
    verdict,
    // As the farmer app's farm page: loading until water or growth has something to say.
    verdictLoading: ndmi.status === 'loading' && (ndvi.status === 'loading' || ndre.status === 'loading'),
    patches,
    pixels,
    history,
    seasons,
    anyFailed: failed(ndmi) || failed(ndvi) || failed(ndre) || failed(ndwi),
    pictureDate: readyData(ndmi)?.date || readyData(ndvi)?.date || null,
    reload,
  };
}
